import {
  DownloadSimpleIcon,
  PrinterIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { logbookEntries } from '@/lib/db';

import { PendingDrafts } from './PendingDrafts';
import { confirmedOnly } from './aggregate';
import {
  commitImportedRows,
  exportForeFlightCsv,
  exportMyFlightbookCsv,
  exportNativeCsv,
  importForeFlightCsv,
  importMyFlightbookCsv,
  importNativeCsv,
} from './interchange';
import type { ImportParseResult } from './interchange';

/**
 * Print, and the CSV interchange formats — `PLAN.md` §6 Phase 6,
 * `NZ-LOGBOOK.md` §6: "import and export both, because a logbook nobody can
 * leave is a logbook nobody should join."
 *
 * Export only ever reads confirmed entries — a draft has not been reviewed,
 * and handing it to another logbook app or a printed page is exactly the
 * "computer-generated flight record entered" case `NZ-LOGBOOK.md` §2.3 is
 * built to keep out of anything that counts.
 *
 * Import is draft-and-approve, the same as an imported track: `commit.ts`
 * writes every parsed row as an unreviewed draft, so nothing from an external
 * file counts toward anything until a pilot has looked at it on the logbook
 * grid.
 */

type Format = 'native' | 'foreflight' | 'myflightbook';

const FORMATS: readonly { id: Format; label: string }[] = [
  { id: 'native', label: 'Flight Data Viewer (full detail)' },
  { id: 'foreflight', label: 'ForeFlight' },
  { id: 'myflightbook', label: 'MyFlightbook' },
];

const EXPORTERS: Record<Format, (entries: ReturnType<typeof confirmedOnly>) => string> = {
  native: exportNativeCsv,
  foreflight: exportForeFlightCsv,
  myflightbook: exportMyFlightbookCsv,
};

const IMPORTERS: Record<Format, (text: string) => ImportParseResult> = {
  native: importNativeCsv,
  foreflight: importForeFlightCsv,
  myflightbook: importMyFlightbookCsv,
};

function download(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function ExportPage() {
  const entries = useLiveQuery(() => logbookEntries.listOrdered(), [], null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importFormat, setImportFormat] = useState<Format>('native');
  const [parsed, setParsed] = useState<ImportParseResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const approvedCount = entries === null ? 0 : confirmedOnly(entries).length;

  const handleExport = (format: Format) => {
    if (entries === null) {
      return;
    }
    const csv = EXPORTERS[format](confirmedOnly(entries));
    download(`logbook-${format}-${new Date().toISOString().slice(0, 10)}.csv`, csv);
  };

  const handleFile = async (file: File) => {
    setResult(null);
    const text = await file.text();
    setParsed(IMPORTERS[importFormat](text));
  };

  const commit = async () => {
    if (parsed === null || parsed.rows.length === 0) {
      return;
    }
    setBusy(true);
    try {
      const summary = await commitImportedRows(parsed.rows);
      setResult(
        `Drafted ${String(summary.imported)} ${summary.imported === 1 ? 'entry' : 'entries'} for review.` +
          (summary.skipped > 0
            ? ` ${String(summary.skipped)} skipped. Already an entry for that aircraft and day.`
            : ''),
      );
      setParsed(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className="border-border bg-surface rounded-container border p-4">
        <h2 className="text-sm font-semibold tracking-tight">Print</h2>
        <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
          CAA 1373 layout. Save as PDF from the browser's print dialog.
        </p>
        <Button asChild className="mt-3">
          <Link to="/logbook/print">
            <PrinterIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Open printable view
          </Link>
        </Button>
      </section>

      <section className="border-border bg-surface rounded-container border p-4">
        <h2 className="text-sm font-semibold tracking-tight">Export</h2>
        <p className="text-muted-foreground mt-1 text-sm leading-relaxed">Approved entries only.</p>
        <PendingDrafts className="mt-1" />
        <div className="mt-3 flex flex-wrap gap-2">
          {FORMATS.map((format) => (
            <Button
              key={format.id}
              variant="outline"
              size="sm"
              disabled={approvedCount === 0}
              onClick={() => {
                handleExport(format.id);
              }}
            >
              <DownloadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
              {format.label}
            </Button>
          ))}
        </div>
      </section>

      <section
        id="import"
        className="border-border bg-surface rounded-container scroll-mt-24 border p-4"
      >
        <h2 className="text-sm font-semibold tracking-tight">Import</h2>
        <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
          ForeFlight, MyFlightbook or Flight Data Viewer CSV. Rows are added as drafts.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={importFormat}
            onChange={(e) => {
              setImportFormat(e.target.value as Format);
              setParsed(null);
              setResult(null);
            }}
            className="border-border bg-surface rounded-control focus-visible:outline-ring border px-2.5 py-1.5 text-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            {FORMATS.map((format) => (
              <option key={format.id} value={format.id}>
                {format.label}
              </option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              fileInputRef.current?.click();
            }}
          >
            <UploadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
            Choose file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="sr-only"
            aria-label="Choose a CSV file to import"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file !== undefined) {
                void handleFile(file);
              }
            }}
          />
        </div>

        {parsed === null ? null : (
          <div className="mt-3 space-y-2">
            <p className="text-sm">
              Found {parsed.rows.length} {parsed.rows.length === 1 ? 'row' : 'rows'} to import
              {parsed.errors.length > 0
                ? `, and ${String(parsed.errors.length)} that could not be read.`
                : '.'}
            </p>
            {parsed.errors.length > 0 ? (
              <ul className="text-danger space-y-0.5 text-xs">
                {parsed.errors.slice(0, 10).map((error, index) => (
                  <li key={index} className="flex items-start gap-1">
                    <WarningCircleIcon
                      aria-hidden="true"
                      weight={ICON_WEIGHT}
                      className="mt-0.5 size-3 shrink-0"
                    />
                    Line {error.line}: {error.message}
                  </li>
                ))}
              </ul>
            ) : null}
            <Button
              size="sm"
              disabled={busy || parsed.rows.length === 0}
              onClick={() => {
                void commit();
              }}
            >
              Import {parsed.rows.length} {parsed.rows.length === 1 ? 'entry' : 'entries'} as drafts
            </Button>
          </div>
        )}

        {result === null ? null : <p className="text-muted-foreground mt-2 text-sm">{result}</p>}
      </section>
    </div>
  );
}
